import type { Publisher } from "@repo/fika-shared/src/loader/publisher-schema.js"
import type { Story } from "@repo/fika-shared/src/loader/story-schema.js"
import { sortBy } from "remeda"
import { createMemo, For, type ParentProps, Show } from "solid-js"
import { useAppContext } from "@/context/app"
import { Box } from "@/designSystem/components/box"
import { Icon } from "@/designSystem/components/icon"
import { Logo } from "@/designSystem/components/logo"
import { NavLink } from "@/designSystem/components/navLink"
import { Text } from "@/designSystem/components/text"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { Card } from "@/designSystem/patterns/card"
import { Info } from "@/designSystem/patterns/info"
import { List } from "@/designSystem/patterns/list"
import { PostComponent } from "@/designSystem/patterns/post"
import { StatsHeader } from "@/designSystem/patterns/stats-header"
import { StoryComponent } from "@/designSystem/patterns/story"
import { useI18nContext } from "@/i18n/i18n-solid"
import { extractDomain } from "@/lib/extractDomain"
import { getTitle } from "@/lib/getTitle"
import { createBlogRouter } from "@/routes/posts-router"
import { createPublishersRouter } from "@/routes/publishers-router"
import { createStoriesRouter } from "@/routes/stories-router"
import { Onboarding } from "./onboarding"
import {
  gradientWrapperClass,
  headerClass,
  itemClass,
  listClass,
  listWrapperClass,
  postChartClass,
  sectionTitleClass,
  textClass,
  titleClass,
  wrapperClass,
} from "./page.css"

export function Dashboard() {
  const { db, user } = useAppContext()
  const publishers = createMemo(discoverPublishers)
  const stories = createMemo(() =>
    Array.from(discoveredStories(), db.story.get).filter(p => !!p),
  )
  const hasContent = createMemo(
    () => !db.story.empty() || !db.publisher.empty(),
  )
  const petName = createMemo(() => user.name || "stranger")

  return (
    <Show
      when={hasContent()}
      fallback={
        <MainLayout>
          <Onboarding />
        </MainLayout>
      }
    >
      <MainLayout header={<Header />}>
        <Box gap="16" class={wrapperClass}>
          <Box padding="24" alignItems="center" class={titleClass}>
            <Text
              fontSize="hero2"
              textAlign="center"
              color="sand900"
              fontWeight="600"
              fontFamily="accent"
              class={textClass}
            >
              welcome back, {petName()}!
            </Text>
          </Box>
          <Show when={user.name}>
            <BlogLatest />
          </Show>
          <PublisherList publishers={publishers()} />
          <StoryList stories={stories()} />
        </Box>
      </MainLayout>
    </Show>
  )
}

function Header() {
  const { LL } = useI18nContext()

  return (
    <nav class={headerClass}>
      <NavLink hue="emerald" href="/bookmarks" iconName="book-marked" wrap>
        {LL().common.bookmarks()}
      </NavLink>
      <NavLink hue="indigo" href="/feeds" iconName="newspaper" wrap>
        {LL().common.feeds()}
      </NavLink>
      <NavLink hue="amber" href="/blog" iconName="pen-tool" wrap>
        {LL().common.blog()}
      </NavLink>
      <NavLink hue="orange" href="/settings" iconName="settings" wrap>
        settings
      </NavLink>
    </nav>
  )
}

function BlogLatest() {
  const { db } = useAppContext()
  const last3Posts = createMemo(() =>
    db.post.sortBy("published_at", "desc").slice(0, 3),
  )
  const { paths } = createBlogRouter()

  return (
    <div class={postChartClass}>
      <Box fill>
        <Box
          gap="8"
          flexDirection="row"
          alignItems="center"
          class={sectionTitleClass}
        >
          <Icon name="pen-tool" color="amber600" />
          <Text fontWeight="500" color="amber900" fontSize="l">
            latest from your blog
          </Text>
        </Box>
        <div class={listWrapperClass}>
          <div class={gradientWrapperClass}>
            <ul class={listClass}>
              <li class={itemClass}>
                <Card href="/blog" hue="amber" expand>
                  <StatsHeader interval="1d" measure="views" />
                </Card>
              </li>
              <Show
                when={last3Posts().length}
                fallback={<Info>you haven't published any articles yet.</Info>}
              >
                <For each={last3Posts()}>
                  {post => (
                    <li class={itemClass}>
                      <Card href={paths().post(post).url} hue="amber" expand>
                        <PostComponent post={post} hideLogo />
                      </Card>
                    </li>
                  )}
                </For>
              </Show>
            </ul>
          </div>
        </div>
      </Box>
    </div>
  )
}

const NEWSLETTER_THRESHOLD = 1000

function discoverPublishers() {
  const { db } = useAppContext()

  const publishersWithBookmarks = db.publisher.all().flatMap(publisher => {
    const subscription = db.subscription.findBy({
      publisher_id: publisher.id,
    })
    if (subscription) return []

    const entry = db.entry.findBy({ publisher_id: publisher.id })
    if (!entry) return []

    const story = entry ? db.story.get(entry.story_id) : null
    if (!story) return []

    const bookmark = db.bookmark.findBy({ story_id: story.id })
    if (!bookmark) return []

    return { publisher, bookmark }
  })

  return sortBy(publishersWithBookmarks, [
    item => item.bookmark.bookmarked_at,
    "desc",
  ]).map(item => item.publisher)
}

function discoveredStories() {
  const { db } = useAppContext()
  const discoveredStories: Set<string> = new Set()

  const entries = db.entry
    .sortBy("published_at", "desc")
    .slice(0, NEWSLETTER_THRESHOLD)

  for (const entry of entries) {
    const bookmark = db.bookmark.findBy({ story_id: entry.story_id })
    if (bookmark) continue

    const publisher = db.publisher.get(entry.publisher_id)
    if (!publisher) continue

    const subscription = db.subscription.findBy({ publisher_id: publisher.id })
    if (!subscription) continue
    if (subscription.silenced) continue

    discoveredStories.add(entry.story_id)
  }

  return discoveredStories
}

function DiscoverPublisher(props: { publisher: Publisher }) {
  const { db } = useAppContext()
  const title = createMemo(() => getTitle(props.publisher.dc_title))
  const { paths } = createPublishersRouter()
  const bookmarks = createMemo(() => {
    const entries = db.entry.filterBy({ publisher_id: props.publisher.id })

    return entries.reduce(
      (memo, entry) =>
        memo + db.bookmark.filterBy({ story_id: entry.story_id }).length,
      0,
    )
  })

  return (
    <Card href={paths().publisher(props.publisher).url} hue="indigo" expand>
      <Box gap="16" fill>
        <Box gap="8" fill>
          <Box>
            <Box flexDirection="row" alignItems="center" gap="8">
              <Logo
                image={{
                  url: props.publisher.logo_url,
                  imageClass: "logo",
                }}
                fallback={<Icon name="newspaper" color="indigo700" />}
                alt={title()}
              />
              <Text
                color="indigo950"
                fontSize="l"
                tight
                fontWeight="500"
                lineClamp="1"
              >
                {title()}
              </Text>
            </Box>
            <Text fontSize="s" color="sand800" lineClamp="1">
              {extractDomain(props.publisher.url)}
            </Text>
          </Box>
          <Show when={props.publisher.dc_description}>
            {description => (
              <Text fontSize="s" color="sand900" lineClamp="3">
                {description()}
              </Text>
            )}
          </Show>
        </Box>
        <Text fontSize="s" color="emerald800" lineClamp="1">
          {bookmarks()} bookmarks
        </Text>
      </Box>
    </Card>
  )
}

type Props = ParentProps & {
  publishers: Publisher[]
}
function PublisherList(props: Props) {
  return (
    <Box>
      <Box
        gap="8"
        flexDirection="row"
        alignItems="center"
        class={sectionTitleClass}
      >
        <Icon name="newspaper" color="indigo700" size="24" />
        <Text color="indigo900" fontSize="l" fontWeight="500" lineClamp="1">
          recommended feeds for you
        </Text>
      </Box>
      <div class={listWrapperClass}>
        <div class={gradientWrapperClass}>
          <Show
            when={props.publishers.length}
            fallback={
              <Info>no feeds found, try bookmarking some stories.</Info>
            }
          >
            <ul class={listClass}>
              <For each={props.publishers}>
                {publisher => (
                  <li class={itemClass}>
                    <DiscoverPublisher publisher={publisher} />
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </div>
      </div>
    </Box>
  )
}

type StoryListProps = ParentProps & {
  stories: Story[]
}
function StoryList(props: StoryListProps) {
  const { paths, listParams } = createStoriesRouter()

  return (
    <Box>
      <Box
        gap="8"
        flexDirection="row"
        alignItems="center"
        class={sectionTitleClass}
      >
        <Icon name="book-marked" color="emerald700" size="24" />
        <Text color="emerald900" fontSize="l" fontWeight="500" lineClamp="1">
          recent stories from your subscriptions
        </Text>
      </Box>
      <div class={listWrapperClass}>
        <Show
          when={props.stories.length}
          fallback={
            <Info>no stories found, try subscribing to some publishers.</Info>
          }
        >
          <List
            list={props.stories}
            pagination={{
              path: page => paths({ page }).url,
              page: listParams().page,
            }}
            content={story => (
              <StoryComponent story={story} namespace="stories" />
            )}
            href={story => paths().story(story).url}
          />
        </Show>
      </div>
    </Box>
  )
}
